import React, {useEffect} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {ArrUtil} from 'yougile-util'
import {DropdownButton} from '@components/common/dropdown-list/dropdown-button'
import {TickIcon} from '@components/common/icons/icons'
import {TriCheckbox} from '@components/common/tri-checkbox/tri-checkbox'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {useBoolean} from '@containers/hooks'
import {cn} from '@containers/html-utils'

export interface Option<T = string> {
  value: T
  label: string
  groupTitle?: string
}

export interface DropdownListProps<T> {
  value: T
  onChange: (value: T) => void
  options: Option<T>[]
  link?: boolean
  disabled?: boolean
  className?: string
  menuListClassName?: string
  large?: boolean
  initialOpen?: boolean
  placeholder?: string
  onOpenChange?: (isOpen: boolean) => void
  dataTestId?: string
}

const TwGroupTitle = tw.div`
  leading-6
  text-12
  text-third-old
  px-8
`

const TwListItem = tw.div`
    min-h-32
    flex
    items-center
    cursor-pointer
`
const TwListItemWithHover = tw(TwListItem)`
    hover:bg-action-hover
    hover:text-on-color
`
const TwList = tw.div`
    inline-flex
    py-4
    flex-col
    text-14
    text-main-old
    cursor-default
    select-none
    max-h-380
`

export interface MenuItem<T> extends Option<T> {
  icon?: React.ReactNode
  count?: string
}

export interface MenuListProps<T> {
  value?: T
  onChange?: (value: T) => void
  items: MenuItem<T>[]
  className?: string
}

export const MenuList = <T,>(props: MenuListProps<T>) => {
  let currentGroupTitle: string | undefined = undefined
  return (
    <WithPerfectScrollbar>
      <TwList className={props.className}>
        {props.items.map((x, i) => {
          const isSelected = props.value === x.value
          let showGroupTitle = false
          if (x.groupTitle !== currentGroupTitle) {
            showGroupTitle = true
            currentGroupTitle = x.groupTitle
          }
          return (
            <React.Fragment key={i}>
              {showGroupTitle && <TwGroupTitle>{x.groupTitle}</TwGroupTitle>}
              <TwListItemWithHover
                onClick={e => {
                  e.stopPropagation()
                  props.onChange?.(x.value)
                }}
                className={cn('pr-16')}
                data-test-id={`list-item-${JSON.stringify(x.value)?.replaceAll(
                  '"',
                  ''
                )}`}
              >
                <div className="shrink-0 m-4">
                  {x.icon ? (
                    x.icon
                  ) : (
                    <TickIcon className={cn(!isSelected && 'invisible')} />
                  )}
                </div>
                <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                  {x.label}
                </span>
              </TwListItemWithHover>
            </React.Fragment>
          )
        })}
      </TwList>
    </WithPerfectScrollbar>
  )
}

export const DropdownList = <T,>(props: DropdownListProps<T>) => {
  const openState = useBoolean(!!props.initialOpen)
  useEffect(() => {
    if (typeof props?.onOpenChange === 'function') {
      props.onOpenChange(openState.value)
    }
  }, [openState.value])
  const list = (
    <MenuList
      value={props.value}
      className={props.menuListClassName}
      items={props.options}
      onChange={value => {
        openState.turnOff()
        props.onChange(value)
      }}
    />
  )
  const currentValue = props.options.find(x => x.value === props.value)
  return (
    <DropdownButton
      onOpenChange={openState.setValue}
      disabled={props.disabled}
      large={props.large}
      open={openState.value}
      link={props.link}
      content={list}
      className={props.className}
      dataTestId={props.dataTestId}
    >
      {currentValue?.label ?? props.placeholder}
    </DropdownButton>
  )
}

export interface DropdownMultiSelectListProps<T> {
  value: T[]
  onChange: (value: T[]) => void
  options: Option<T>[]
  link?: boolean
  className?: string
  large?: boolean
  allText?: string
  initialOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  placeholder?: string
  withoutAll?: boolean
}

export const getMultiSelectText = ({
  value,
  options,
  allText = Lang.msg.component.dropdown.all,
  placeholder
}: {
  value: any[]
  options: Option<any>[]
  allText?: string
  placeholder?: string
}) => {
  if (value.length === 0) {
    return placeholder ?? Lang.msg.component.dropdown.notSelected
  }
  if (value.length === options.length) {
    return (allText ?? placeholder) || ''
  }
  if (value.length === 1) {
    return options.find(x => x.value === value[0])?.label
  }
  return `${Lang.msg.component.dropdown.selected} ${value.length} ${Lang.msg.component.dropdown.outOf} ${options.length}`
}

// eslint-disable-next-line max-lines-per-function
export const DropdownMultiSelectList = <T,>({
  allText = Lang.msg.component.dropdown.all,
  placeholder,
  withoutAll,
  ...props
}: DropdownMultiSelectListProps<T>) => {
  const openState = useBoolean(!!props.initialOpen)
  useEffect(() => {
    if (typeof props?.onOpenChange === 'function') {
      props.onOpenChange(openState.value)
    }
  }, [openState.value])
  const allState =
    props.value.length === props.options.length
      ? true
      : props.value.length === 0
      ? false
      : undefined
  const list = (
    <WithPerfectScrollbar>
      <TwList>
        {!withoutAll && (
          <TwListItem
            key="all"
            onClick={() => {
              const newValue = allState ? [] : props.options.map(x => x.value)
              props.onChange(newValue)
            }}
            className="pl-8 pr-16"
          >
            <TriCheckbox className="mr-8" value={allState} />
            <span>{placeholder ?? allText}</span>
          </TwListItem>
        )}
        {props.options.map((x, i) => {
          const isSelected = props.value.includes(x.value)
          return (
            <TwListItem
              key={i}
              onClick={() => {
                const newValueSet = new Set(ArrUtil.xor(props.value, [x.value]))
                const newValue = ArrUtil.filterMap(props.options, x =>
                  newValueSet.has(x.value) ? x.value : null
                )
                props.onChange(newValue)
              }}
              className="pl-8 pr-16"
            >
              <TriCheckbox className="mr-8" value={isSelected} />
              <span>{x.label}</span>
            </TwListItem>
          )
        })}
      </TwList>
    </WithPerfectScrollbar>
  )
  return (
    <DropdownButton
      onOpenChange={openState.setValue}
      large={props.large}
      open={openState.value}
      link={props.link}
      content={list}
      className={props.className}
    >
      {getMultiSelectText({
        value: props.value,
        options: props.options,
        allText,
        placeholder
      })}
    </DropdownButton>
  )
}
